import { toRaw } from 'vue'
import type { AdvancedTextImportRow, BasicTextImportRow } from '@/features/text-import/textImportLogic'

export const TEXT_IMPORT_ROW_HEIGHT = 36
export const TEXT_IMPORT_CONTENT_LINE_HEIGHT = 28
export const TEXT_IMPORT_ROW_PADDING = 8
export const TEXT_IMPORT_VIRTUAL_LIST_HEIGHT = 510
/** 结果表行末「更多」列宽（与表头、行组件 grid 一致）。 */
export const TEXT_IMPORT_ROW_MORE_COL = '40px'
export const TEXT_IMPORT_BASIC_GRID_COLUMNS =
  `110px 180px 180px 90px minmax(220px, 1fr) ${TEXT_IMPORT_ROW_MORE_COL}`
/** 与表头、TextImportResultRow 高级模式列数一致：类型 + 剧情名 + BGM + 背景 + 姓名 + 文本 + 更多 */
export const TEXT_IMPORT_ADVANCED_GRID_COLUMNS =
  `72px 100px 150px 150px 80px minmax(240px, 1fr) ${TEXT_IMPORT_ROW_MORE_COL}`
/** 高级结果表最小宽度，表头与行共用，避免仅表体横向撑开导致列错位。 */
export const TEXT_IMPORT_ADVANCED_TABLE_MIN_WIDTH = '832px'
export const TEXT_IMPORT_BASIC_TABLE_MIN_WIDTH = '820px'

/** 高级结果表「类型」列文案（仅选项相关行显示，对话/剧情留空）。 */
export function getAdvancedTextImportRowTypeLabel(row: AdvancedTextImportRow): string {
  if (row.optionKey.type === 'start') return '选项开始'
  if (row.optionKey.type === 'end') return '选项结束'
  if (row.optionKey.type === 'item') return '选项'
  return ''
}

/** 选项开始 / 选项 / 选项结束行。 */
export function isAdvancedOptionStructuralRow(row: AdvancedTextImportRow): boolean {
  const type = row.optionKey.type
  return type === 'start' || type === 'item' || type === 'end'
}

export function canAdvancedTextImportRowCopy(row: AdvancedTextImportRow): boolean {
  return !isAdvancedOptionStructuralRow(row)
}

export function canAdvancedTextImportRowDelete(row: AdvancedTextImportRow): boolean {
  return !isAdvancedOptionStructuralRow(row)
}

/** 选项开始行不可在其后粘贴；选项 / 选项结束行可粘贴。 */
export function canAdvancedTextImportRowPasteAt(row: AdvancedTextImportRow): boolean {
  return row.optionKey.type !== 'start'
}

export type TextImportVirtualRow<T> = {
  row: T
  index: number
  top: number
  height: number
}

/** 统计显示文本占用的行数（按 `\n` 分段）。 */
export function getContentLineCount(content: string): number {
  if (!content.trim()) return 1
  return Math.max(1, content.split('\n').length)
}

export function getContentTextareaHeight(content: string): number {
  return getContentLineCount(content) * TEXT_IMPORT_CONTENT_LINE_HEIGHT
}

/** 初级结果表单行高度。 */
export function getBasicRowHeight(row: BasicTextImportRow): number {
  return Math.max(
    TEXT_IMPORT_ROW_HEIGHT,
    getContentLineCount(row.content) * TEXT_IMPORT_CONTENT_LINE_HEIGHT + TEXT_IMPORT_ROW_PADDING,
  )
}

/** 高级结果表单行高度（选项行固定高度，文本行随行数增高）。 */
export function getAdvancedRowHeight(row: AdvancedTextImportRow): number {
  if (row.optionKey.type === 'start' || row.optionKey.type === 'end') return TEXT_IMPORT_ROW_HEIGHT
  if (row.optionKey.type === 'item') {
    return Math.max(
      TEXT_IMPORT_ROW_HEIGHT,
      getContentLineCount(row.optionContent) * TEXT_IMPORT_CONTENT_LINE_HEIGHT + TEXT_IMPORT_ROW_PADDING,
    )
  }
  return Math.max(
    TEXT_IMPORT_ROW_HEIGHT,
    getContentLineCount(row.textContent) * TEXT_IMPORT_CONTENT_LINE_HEIGHT + TEXT_IMPORT_ROW_PADDING,
  )
}

function buildRowOffsets<T>(list: T[], getHeight: (row: T) => number): number[] {
  const offsets: number[] = []
  let acc = 0
  for (const row of list) {
    offsets.push(acc)
    acc += getHeight(row)
  }
  return offsets
}

/**
 * 按滚动位置计算虚拟列表可见行。
 * @param list 全量结果行。
 * @param scrollTop 列表滚动偏移。
 * @param getHeight 单行高度计算。
 */
export function createTextImportVirtualRows<T>(
  list: T[],
  scrollTop: number,
  getHeight: (row: T) => number,
): TextImportVirtualRow<T>[] {
  if (!list.length) return []
  const offsets = buildRowOffsets(list, getHeight)
  let start = 0
  for (let i = 0; i < list.length; i += 1) {
    const rowTop = offsets[i] ?? 0
    const rowHeight = getHeight(list[i]!)
    if (rowTop + rowHeight >= scrollTop - TEXT_IMPORT_ROW_HEIGHT * 2) {
      start = i
      break
    }
  }
  const visibleBottom = scrollTop + TEXT_IMPORT_VIRTUAL_LIST_HEIGHT + TEXT_IMPORT_ROW_HEIGHT * 2
  const result: TextImportVirtualRow<T>[] = []
  for (let i = start; i < list.length; i += 1) {
    const row = list[i]!
    const top = offsets[i] ?? 0
    if (top > visibleBottom && result.length) break
    result.push({
      row,
      index: i,
      top,
      height: getHeight(row),
    })
  }
  return result
}

/** 虚拟列表占位总高度。 */
export function getTextImportVirtualListHeight<T>(list: T[], getHeight: (row: T) => number): number {
  return list.reduce((sum, row) => sum + getHeight(row), 0)
}

function createRowId(): string {
  return Math.random().toString(16).slice(2)
}

/** 复制初级结果行并生成新 id（先 toRaw，避免 structuredClone 无法克隆 Proxy）。 */
export function cloneBasicTextImportRow(row: BasicTextImportRow): BasicTextImportRow {
  const plain = toRaw(row)
  return {
    ...structuredClone(plain),
    id: createRowId(),
  }
}

/** 复制高级结果行并生成新 uid（先 toRaw，避免 structuredClone 无法克隆 Proxy）。 */
export function cloneAdvancedTextImportRow(row: AdvancedTextImportRow): AdvancedTextImportRow {
  const plain = toRaw(row)
  return {
    ...structuredClone(plain),
    uid: createRowId(),
  }
}
